import { useCallback, useEffect, useState } from 'react';

const SAFETY_SUMMARY_VISIBILITY_KEY = 'ironview:safety:summary-visible';
const MOBILE_SUMMARY_MEDIA_QUERY = '(max-width: 639px)';

const readStoredVisibility = (): boolean | null => {
  if (typeof window === 'undefined') return null;

  try {
    const storedValue = window.sessionStorage.getItem(SAFETY_SUMMARY_VISIBILITY_KEY);
    if (storedValue === 'true') return true;
    if (storedValue === 'false') return false;
  } catch {
    // Storage can be unavailable in restricted browser contexts.
  }

  return null;
};

export const useSafetySummaryVisibility = () => {
  const [visibilityOverride, setVisibilityOverride] = useState<boolean | null>(
    readStoredVisibility
  );
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_SUMMARY_MEDIA_QUERY).matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_SUMMARY_MEDIA_QUERY);
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  const isSummaryVisible = visibilityOverride ?? !isMobileViewport;
  const toggleSummary = useCallback(() => {
    setVisibilityOverride((current) => {
      const next = !(current ?? !isMobileViewport);
      try {
        window.sessionStorage.setItem(SAFETY_SUMMARY_VISIBILITY_KEY, String(next));
      } catch {
        // Keep the current-page behavior when storage is unavailable.
      }
      return next;
    });
  }, [isMobileViewport]);

  return { isSummaryVisible, toggleSummary };
};
